import {Institution} from '@/interfaces/institution.interface';
import {LeanDocument, Types} from 'mongoose';
import {API, ApiRequestOptions} from './api.class';
import {CreateInstitutionRequestDto} from '../dto/institutioner/request/createInstitution.request.dto';
import {
    GetInstitutionPlanningListRequestDTO,
} from '@/classes/dto/institutioner/request/getInstitutionPlanningList.request.dto';
import {
    GetInstitutionPlanningListResponseDTO,
} from '@/classes/dto/institutioner/response/getInstitutionPlanningList.response.dto';
import {FindInstitutionRequestDto} from '@/classes/dto/institutioner/request/FindInstitution.request.dto';
import {UpdateInstitutionRequestDto} from '@/classes/dto/institutioner/request/UpdateInstitution.request.dto';
import {
    InstitutionEmployeeAssignmentRequestDto,
} from '@/classes/dto/institutioner/request/InstitutionEmployeeAssignment.request.dto';
import {
    AddOrRemoveResponsibleRequestDto,
} from '@/classes/dto/responsible-handler/request/AddOrRemoveResponsible.request.dto';
import {FindByIdOptionsRequestDto} from '@/classes/dto/_common/request/FindByIdOptions.request.dto';
import {DeactivationRequestDto} from '@/classes/dto/_common/request/Deactivation.request.dto';
import {ActivationRequestDto} from '@/classes/dto/_common/request/Activation.request.dto';
import {
    FindStudentsByInstitutionRequestDto,
} from '@/classes/dto/institutioner/request/FindStudentsByInstitution.request.dto';
import {
    FindStudentsByInstitutionResponseDto,
} from '@/classes/dto/institutioner/response/FindStudentsByInstitution.response.dto';
import {GetDirectionResponseDto} from '@/classes/dto/geocoder/response/GetDirections.response.dto';
import {FindInstitutionFilter} from '@/classes/dto/institutioner/request/filters/FindInstitution.filter';
import {GeoNearRequest} from '@/classes/dto/_common/request/GeoNearRequest';
import {
    CreateAcquisitionListRequestDto,
} from '@/classes/dto/institutioner/acquisition/request/CreateAcquisitionList.request.dto';
import {
    FindAcquisitionListRequestDto,
} from '@/classes/dto/institutioner/acquisition/request/FindAcquisitionList.request.dto';
import {AcquisitionList} from '@/documentTypes/institutioner/acquisition/AcquisitionList.document';
import {AcquisitionCandidate} from '@/interfaces/acquisitionCandidate.interface';
import {
    UpdateAcquisitionListRequestDto,
} from '@/classes/dto/institutioner/acquisition/request/UpdateAcquisitionList.request.dto';
import {
    PullAcquisitionListInstitutionItemParams,
} from '@/classes/dto/institutioner/acquisition/request/PullAcquisitionListInstitutionItemParams.request.dto';
import {BulkUpdateResponseDto} from '@/classes/dto/_common/response/bulkUpdate.response.dto';
import {BulkUpdateInstitutionRequestDto} from '@/classes/dto/institutioner/request/BulkUpdateInstitution.request.dto';
import {
    ImportInstitutionsFromExternalInstanceRequestDto,
} from '@/classes/dto/institutioner/request/ImportInstitutionsFromExternalInstance.request.dto';
import {
    ConfirmCourseAllocationRequestDto,
} from '@/classes/dto/institutioner/course-allocation/ConfirmCourseAllocation.request.dto';
import {
    ConfirmCourseAllocationResponseDto,
} from '@/classes/dto/institutioner/course-allocation/ConfirmCourseAllocation.response.dto';
import {
    FindOpenInstitutionRequestDto,
} from '@/classes/dto/institutioner/request/FindOpenInstitution.request.dto';
import {
    FindOpenInstitutionsResponse,
} from '@/classes/dto/institutioner/response/FindOpenInstitutions.response.dto';
import {
    OpenInstitutionsStatsResponse,
} from '@/classes/dto/institutioner/response/OpenInstitutionsStats.response.dto';
import {
    GetOpenInstitutionsStatsRequestDto,
} from '@/classes/dto/institutioner/request/GetOpenInstitutionsStats.request.dto';

/**
 * This class is used for communicating with Institutioner microservice
 * @class InstitutionerApi
 * @author mklaczinski
 */
export class InstitutionerApi extends API {
    static service = 'institutioner';

    static courseAllocation = {
        confirm: async (data: ConfirmCourseAllocationRequestDto): Promise<ConfirmCourseAllocationResponseDto> => {
            // 409: finalization of this institution is already running (newer backends) - handled by the dialog
            return InstitutionerApi.post(`course-allocation/confirm`, data, undefined, {handledStatuses: [409]});
        },
    };

    static acquisition = {
        create: async (data: CreateAcquisitionListRequestDto): Promise<AcquisitionList> => {
            return InstitutionerApi.post(`acquisition`, data);
        },
        /** options.handledStatuses: statuses the caller handles itself (e.g. 403 for an optional card) */
        find: async (data: FindAcquisitionListRequestDto, options?: ApiRequestOptions): Promise<{
            acquisitionLists: AcquisitionList[];
            maxPage: number
        }> => {
            return InstitutionerApi.get(`acquisition/find`, data, undefined, options);
        },
        findById: async (id: Types.ObjectId, options?: FindByIdOptionsRequestDto): Promise<AcquisitionList> => {
            return InstitutionerApi.get(`acquisition/find/${id.toString()}`, options);
        },
        /** Ranked suggestions for whom a new acquisition list is worthwhile */
        candidates: async (data: { limit?: number; defaultRadiusKm?: number } = {}): Promise<AcquisitionCandidate[]> => {
            return InstitutionerApi.get('acquisition/candidates', data);
        },
        update: async (id: Types.ObjectId, data: UpdateAcquisitionListRequestDto): Promise<void> => {
            return InstitutionerApi.put(`acquisition/update/${id.toString()}`, data);
        },
        remove: async (id: Types.ObjectId): Promise<void> => {
            return InstitutionerApi.delete(`acquisition/${id.toString()}`);
        },
        /** Sets the Folgegespräch owner of one school; null falls back to the owner of the list */
        setItemMeetingOwner: async (listId: Types.ObjectId | string, institutionId: Types.ObjectId | string, meetingOwner: string | null): Promise<void> => {
            return InstitutionerApi.put(`acquisition/meetingOwner/${listId.toString()}/${institutionId.toString()}`, {meetingOwner});
        },
        pullInstituion: async (data: PullAcquisitionListInstitutionItemParams): Promise<void> => {
            return InstitutionerApi.put(`acquisition/pull/${data.listId}/${data.institutionId}`, {});
        },

    }

    static openInstitutions = {
        find: async (data: FindOpenInstitutionRequestDto): Promise<FindOpenInstitutionsResponse> => {
            return InstitutionerApi.get(`open-institutions/aggregation`, data);
        },
        getStats: async (data?: GetOpenInstitutionsStatsRequestDto): Promise<OpenInstitutionsStatsResponse> => {
            return InstitutionerApi.get(`open-institutions/stats`, data);
        },
    };

    static async importFromExternalInstance(data: ImportInstitutionsFromExternalInstanceRequestDto) {
        return InstitutionerApi.post(`import-from-external-instance`, data);
    }

    static async import(newDocuments: Institution[]): Promise<Institution[]> {
        return this.post('import', newDocuments);
    }

    static async create(newDocument: CreateInstitutionRequestDto): Promise<Institution> {
        return this.post('', newDocument);
    }

    static async update(id: Types.ObjectId, updateQuery: UpdateInstitutionRequestDto): Promise<void> {
        return this.put(`${id}`, updateQuery);
    }

    static async bulkUpdate(bulkUpdateData: BulkUpdateInstitutionRequestDto): Promise<BulkUpdateResponseDto> {
        return this.put(`bulk`, bulkUpdateData);
    }

    static async addAssignment(id: Types.ObjectId, updateQuery: InstitutionEmployeeAssignmentRequestDto): Promise<void> {
        return this.put(`addAssignment/${id}`, updateQuery);
    }

    static async removeAssignment(id: Types.ObjectId, updateQuery: InstitutionEmployeeAssignmentRequestDto): Promise<void> {
        return this.put(`removeAssignment/${id}`, updateQuery);
    }

    static async addResponsible(id: Types.ObjectId, updateQuery: AddOrRemoveResponsibleRequestDto): Promise<void> {
        return this.put(`addResponsible/${id}`, updateQuery);
    }

    static async removeResponsible(id: Types.ObjectId, updateQuery: AddOrRemoveResponsibleRequestDto): Promise<void> {
        return this.put(`removeResponsible/${id}`, updateQuery);
    }

    static async find(findQuery: FindInstitutionRequestDto & {
        filter: FindInstitutionFilter & { near: GeoNearRequest & { useExactDistance: true } };
    }, options?: ApiRequestOptions): Promise<{ institutions: Array<LeanDocument<Institution> & GetDirectionResponseDto>; maxPage: number }>;

    static async find(findQuery: FindInstitutionRequestDto, options?: ApiRequestOptions): Promise<{ institutions: Institution[]; maxPage: number }>;

    /** options.handledStatuses: statuses the caller handles itself (e.g. 403 for optional lookups) */
    static async find(findQuery: FindInstitutionRequestDto, options?: ApiRequestOptions): Promise<{
        institutions: Institution[] | Array<LeanDocument<Institution> & GetDirectionResponseDto>;
        maxPage: number;
    }> {
        return this.get(``, findQuery, undefined, options);
    }

    /** requestOptions.handledStatuses: e.g. 403 for optional lookups the caller handles itself */
    static async findById(id: Types.ObjectId, options?: FindByIdOptionsRequestDto, requestOptions?: ApiRequestOptions): Promise<Institution> {
        return this.get(id.toString(), options, undefined, requestOptions);
    }

    static async getPlanningList(data?: GetInstitutionPlanningListRequestDTO): Promise<GetInstitutionPlanningListResponseDTO> {
        return this.get(`planning-list`, data);
    }

    static async remove(id: Types.ObjectId): Promise<void> {
        return this.delete(id.toString());
    }

    static async deactivateInstitution(id: Types.ObjectId, data: DeactivationRequestDto): Promise<void> {
        return this.put(`deactivate/${id}`, data);
    }

    static async reactivateInstitution(id: Types.ObjectId, data: ActivationRequestDto): Promise<void> {
        return this.put(`reactivate/${id}`, data);
    }

    static async studentByInstitution(id: Types.ObjectId, data: FindStudentsByInstitutionRequestDto): Promise<FindStudentsByInstitutionResponseDto> {
        return this.get(`studentsByInstitution/${id}`, data);
    }
}
